import { useState, useEffect } from 'react';
import { Download, Trash2, FileType, Calendar, Layers, Home, ChevronRight, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/api.config';
import { useAuthStore } from '../store/authStore';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

interface ConvertedPptx {
  _id: string;
  fileName: string;
  originalFileName: string;
  slidesPerPage: 1 | 2 | 3 | 4;
  fileType?: 'pdf' | 'pptx'; // NPPTXC-RQ-5: merged editable PPTX records
  fileSize: number;
  convertedAt: string;
}

export default function ConvertedPptxList() {
  const { user } = useAuthStore();
  const userPermissions = user?.permissions || [];
  const canDelete = userPermissions.includes('pptx_history_delete');

  const [conversions, setConversions] = useState<ConvertedPptx[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });

  useEffect(() => {
    fetchConversions();
  }, [page]);

  const fetchConversions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/pptx-converter/list?page=${page}&limit=${limit}`);
      const data = await response.json();

      if (data.success) {
        setConversions(data.data);
        if (data.pagination) {
          setPagination({
            total: data.pagination.total,
            totalPages: data.pagination.totalPages,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching conversions:', error);
      Swal.fire('Error', 'Failed to load conversion history', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (conversionId: string) => {
    // Open PDF in new tab for inline viewing (not download)
    const viewUrl = `${API_BASE_URL}/pptx-converter/view/${conversionId}`;
    window.open(viewUrl, '_blank');
  };

  const handleDownload = async (conversionId: string, fileName: string) => {
    try {
      setDownloadingId(conversionId);

      const response = await fetch(`${API_BASE_URL}/pptx-converter/download/${conversionId}`);

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      Swal.fire('Success', 'PDF downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading conversion:', error);
      Swal.fire('Error', 'Failed to download PDF', 'error');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async (conversionId: string, fileName: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete Conversion?',
      itemName: fileName,
    });

    if (confirmed) {
      try {
        const response = await fetch(`${API_BASE_URL}/pptx-converter/${conversionId}`, {
          method: 'DELETE'
        });

        const data = await response.json();

        if (data.success) {
          showDeleteSuccess('Conversion has been deleted successfully.');
          // Reset to page 1 if current page becomes empty after deletion
          if (conversions.length === 1 && page > 1) {
            setPage(page - 1);
          } else {
            fetchConversions(); // Refresh list
          }
        } else {
          throw new Error(data.error);
        }
      } catch (error) {
        console.error('Error deleting conversion:', error);
        showDeleteError('Failed to delete conversion');
      }
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading conversion history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Slides</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">PPTX Conversion History</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <FileType className="w-8 h-8" />
          PPTX Conversion History
        </h1>
        <p className="mt-2 text-gray-600">
          Download or manage your previously converted PowerPoint files
        </p>
      </div>

      {/* Stats */}
      {conversions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-semibold">Total Conversions</p>
                <p className="text-2xl font-bold text-blue-900 mt-1">{conversions.length}</p>
              </div>
              <FileType className="w-12 h-12 text-blue-600 opacity-50" />
            </div>
          </div>
          <div className="bg-orange-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-semibold">Total Size</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">
                  {formatFileSize(conversions.reduce((sum, conv) => sum + conv.fileSize, 0))}
                </p>
              </div>
              <Layers className="w-12 h-12 text-orange-600 opacity-50" />
            </div>
          </div>
          <div className="bg-green-50 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-semibold">Latest</p>
                <p className="text-sm font-bold text-green-900 mt-1">
                  {formatDate(conversions[0].convertedAt)}
                </p>
              </div>
              <Calendar className="w-12 h-12 text-green-600 opacity-50" />
            </div>
          </div>
        </div>
      )}

      {/* Conversions List */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {conversions.length === 0 ? (
          <div className="text-center py-12">
            <FileType className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No conversions yet</p>
            <p className="text-gray-400 text-sm mt-2">Convert your first PPTX file from the PPTX Converter page</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">File Name</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Slides/Page</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Size</th>
                  <th className="text-left py-4 px-4 font-semibold text-gray-700">Converted At</th>
                  <th className="text-center py-4 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {conversions.map((conversion) => (
                  <tr key={conversion._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-3">
                        <FileType className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">{conversion.originalFileName}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Layers className="w-4 h-4 text-orange-600" />
                        <span className="text-gray-700">{conversion.slidesPerPage}</span>
                        {(conversion.fileType === 'pptx' || conversion.fileName.toLowerCase().endsWith('.pptx')) && (
                          <span className="px-2 py-0.5 text-xs font-bold bg-orange-100 text-orange-700 rounded">PPTX</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-gray-600 text-sm">{formatFileSize(conversion.fileSize)}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600 text-sm">{formatDate(conversion.convertedAt)}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleView(conversion._id)}
                          className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                          title="View PDF"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownload(
                            conversion._id,
                            conversion.fileType === 'pptx' || conversion.fileName.toLowerCase().endsWith('.pptx')
                              ? conversion.originalFileName
                              : conversion.originalFileName.replace(/\.pptx$/i, `_${conversion.slidesPerPage}_slides.pdf`)
                          )}
                          disabled={downloadingId === conversion._id}
                          className="p-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Download"
                        >
                          {downloadingId === conversion._id ? (
                            <div className="w-5 h-5 border-2 border-blue-700 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Download className="w-5 h-5" />
                          )}
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(conversion._id, conversion.originalFileName)}
                            className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && conversions.length > 0 && pagination.totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            Previous
          </button>

          <div className="flex gap-1">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  pageNum === page
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page === pagination.totalPages}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
